import { useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Blocking gate: a Language Interpreter must accept the confidentiality statement before using the app.
export function ConfidentialityGate() {
  const { user, setUser } = useAuth();
  const [agree, setAgree] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!user || user.role !== "language_interpreter" || user.confidentiality_accepted_at) return null;

  const accept = async () => {
    setSaving(true);
    try {
      await api.post("/me/acknowledge-confidentiality");
      setUser({ ...user, confidentiality_accepted_at: new Date().toISOString(), confidentiality_required: false, status: "active" });
      toast.success("Thank you — access granted.");
    } catch (e) { toast.error("Could not record your acknowledgement. Please try again."); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-ink/70 flex items-center justify-center p-4" data-testid="confidentiality-gate">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[88vh] overflow-y-auto p-6">
        <h2 className="font-heading font-bold text-2xl text-ink flex items-center gap-2 mb-1"><ShieldCheck className="w-6 h-6 text-clinical" /> Confidentiality &amp; Information Governance</h2>
        <p className="text-sm text-muted2 mb-4">Acknowledgement for Language Interpreters — you must accept this before your account is activated.</p>
        <div className="text-sm text-ink space-y-3 border border-line rounded-md p-4 bg-sand/40">
          <p>By accepting this role and accessing the system, I acknowledge and agree that:</p>
          <p><b>Patient Confidentiality</b> — All patient information I see, hear or access is strictly confidential and used only to provide interpretation during authorised consultations.</p>
          <p><b>Minimum Necessary Access</b> — I will only access information required for my assigned consultation and will not seek out any other patient, appointment or record.</p>
          <p><b>Non-Disclosure</b> — I will not disclose confidential information to any unauthorised person, nor discuss it where it may be overheard.</p>
          <p><b>Information Security</b> — I will protect my login credentials, never share access, and immediately report any suspected breach or lost credentials.</p>
          <p><b>Devices &amp; Recordings</b> — I will not photograph, screenshot, download, copy, print or record patient information or consultations unless expressly authorised with documented consent.</p>
          <p><b>Professional Conduct</b> — I will interpret accurately and impartially, maintain professional boundaries, and never use information for personal benefit.</p>
          <p><b>Compliance &amp; Monitoring</b> — I will comply with all information-governance, data-protection and security policies, and understand all activity may be monitored and audited.</p>
          <p><b>Reporting &amp; Termination</b> — I will report any confidentiality or security concern immediately, and understand my access may be suspended or revoked for non-compliance.</p>
          <p className="text-muted2 text-xs">Non-compliance may result in removal of access, disciplinary action, termination of services, or reporting to relevant authorities. (Version 1.0)</p>
        </div>
        <label className="flex items-center gap-2 mt-4 text-sm font-semibold text-ink cursor-pointer">
          <input type="checkbox" data-testid="confidentiality-agree" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="w-4 h-4 accent-clinical" />
          I have read, understood, and agree to comply with these requirements.
        </label>
        <button onClick={accept} disabled={!agree || saving} data-testid="confidentiality-accept-btn"
          className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-clinical text-white rounded-md px-5 py-3 font-semibold disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Accept &amp; activate my account
        </button>
      </div>
    </div>
  );
}
